/**
 * Version Compare Modal Component
 */
import React, { useState, useEffect } from 'react';
import {
  Modal,
  Typography,
  Space,
  Tag,
  Spin,
  Empty,
  Statistic,
  Row,
  Col,
  Card,
  Divider,
} from 'antd';
import {
  DiffOutlined,
  PlusOutlined,
  MinusOutlined,
  FileTextOutlined,
  ArrowRightOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import versionApi from '../services/versionApi';

const { Text, Title, Paragraph } = Typography;

function formatFileSize(bytes) {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

function VersionCompareModal({ visible, onClose, documentId, versionId1, versionId2 }) {
  const [loading, setLoading] = useState(false);
  const [comparison, setComparison] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (visible && documentId && versionId1 && versionId2) {
      loadComparison();
    }
  }, [visible, documentId, versionId1, versionId2]);

  const loadComparison = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await versionApi.compareVersions(documentId, versionId1, versionId2);
      setComparison(data);
    } catch (err) {
      console.error('Failed to load comparison:', err);
      setError(err.response?.data?.detail || 'Failed to compare versions');
    } finally {
      setLoading(false);
    }
  };

  const renderDiff = (diff) => {
    if (!diff) return <Empty description="No differences found" />;

    const lines = diff.split('\n');
    return (
      <div
        style={{
          fontFamily: 'monospace',
          fontSize: '12px',
          backgroundColor: '#f5f5f5',
          padding: '16px',
          borderRadius: '8px',
          maxHeight: '400px',
          overflow: 'auto',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-all',
        }}
      >
        {lines.map((line, index) => {
          let backgroundColor = 'transparent';
          let color = '#000';

          if (line.startsWith('+++') || line.startsWith('---')) {
            backgroundColor = '#e8e8e8';
            color = '#666';
          } else if (line.startsWith('+')) {
            backgroundColor = '#e6ffed';
            color = '#22863a';
          } else if (line.startsWith('-')) {
            backgroundColor = '#ffeef0';
            color = '#cb2431';
          } else if (line.startsWith('@@')) {
            backgroundColor = '#f1f8ff';
            color = '#0366d6';
          }

          return (
            <div
              key={index}
              style={{
                backgroundColor,
                color,
                padding: '2px 8px',
                marginLeft: '-8px',
                marginRight: '-8px',
              }}
            >
              {line || '\u00A0'}
            </div>
          );
        })}
      </div>
    );
  };

  const VersionInfo = ({ version, label }) => (
    <Card size="small" style={{ flex: 1 }}>
      <div style={{ textAlign: 'center' }}>
        <Tag color={version.is_current ? 'green' : 'default'}>
          {label}
        </Tag>
        <Title level={4} style={{ margin: '8px 0' }}>
          Version {version.version_number}
        </Title>
        <Text type="secondary" style={{ display: 'block' }}>
          {version.created_at
            ? dayjs(version.created_at).format('MMM D, YYYY h:mm A')
            : 'Unknown date'}
        </Text>
        <Text type="secondary" style={{ display: 'block', marginTop: '4px' }}>
          {formatFileSize(version.file_size_bytes)} | {version.chunk_count || 0} chunks
        </Text>
      </div>
    </Card>
  );

  return (
    <Modal
      title={
        <Space>
          <DiffOutlined />
          <span>Compare Versions</span>
        </Space>
      }
      open={visible}
      onCancel={onClose}
      width={900}
      footer={null}
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spin size="large" />
          <p style={{ marginTop: '16px' }}>Comparing versions...</p>
        </div>
      ) : error ? (
        <Empty description={error} />
      ) : comparison ? (
        <div>
          <Row gutter={16} style={{ marginBottom: '24px' }}>
            <Col span={11}>
              <VersionInfo version={comparison.version_1} label="From" />
            </Col>
            <Col span={2} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ArrowRightOutlined style={{ fontSize: '24px', color: '#1890ff' }} />
            </Col>
            <Col span={11}>
              <VersionInfo version={comparison.version_2} label="To" />
            </Col>
          </Row>

          <Divider>Change Summary</Divider>

          <Row gutter={16} style={{ marginBottom: '24px' }}>
            <Col span={8}>
              <Card size="small">
                <Statistic
                  title="Lines Added"
                  value={comparison.stats.lines_added}
                  prefix={<PlusOutlined style={{ color: '#52c41a' }} />}
                  valueStyle={{ color: '#52c41a' }}
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card size="small">
                <Statistic
                  title="Lines Removed"
                  value={comparison.stats.lines_removed}
                  prefix={<MinusOutlined style={{ color: '#ff4d4f' }} />}
                  valueStyle={{ color: '#ff4d4f' }}
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card size="small">
                <Statistic
                  title="Size Change"
                  value={formatFileSize(Math.abs(comparison.stats.size_change))}
                  prefix={
                    comparison.stats.size_change >= 0 ? (
                      <PlusOutlined style={{ color: '#1890ff' }} />
                    ) : (
                      <MinusOutlined style={{ color: '#faad14' }} />
                    )
                  }
                  valueStyle={{
                    color: comparison.stats.size_change >= 0 ? '#1890ff' : '#faad14',
                  }}
                />
              </Card>
            </Col>
          </Row>

          <Divider>
            <Space>
              <FileTextOutlined />
              <span>Content Differences</span>
            </Space>
          </Divider>

          {comparison.diff ? (
            renderDiff(comparison.diff)
          ) : (
            <Empty description="No text differences found (files may be binary or identical)" />
          )}
        </div>
      ) : null}
    </Modal>
  );
}

export default VersionCompareModal;
